import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { cls } from "app/utils";
import Button from "app/components/Button/ButtonVariant";
import { useActions, useSelector } from "app/utils/hooks";
import {
  changeNotifications,
  fetchNotifications,
} from "app/actions/user-settings";
import UserSettingsModel from "app/models/UserSettingsModel";
import { RootState } from "typedefs";
import AccountWrap from "..";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { Toast } from "app/components/helpers";
import CheckBox from "app/components/inputs/CheckBox";
import { schedulingFetchGet, schedulingFetchPatch } from "app/utils/request/scheduling/fetch";
import { allSettledPromise, Settled } from "app/utils/promiseAllSettled";
import UserModel from "app/models/UserModel";
import { hasFeature } from "app/utils/hasFeature";
import ToolTip from "app/routes/Scheduling/ToolTip";

interface UserNotificationsPolicy {
  academySessionEmails: boolean;
  academyProgramEmails: boolean;
  academySubscriptionEmails: boolean;
}

const mapNotificationsPolicyToState = (apiResponse: any): UserNotificationsPolicy => ({
  academySessionEmails: apiResponse.academySessionEmails ?? false,
  academyProgramEmails: apiResponse.academyProgramEmails ?? false,
  academySubscriptionEmails: apiResponse.academySubscriptionEmails ?? false,
});

const fetchUserPolicy = async (user: UserModel) => {
  // fetch policy for athlete
  if (user.kind === "athlete") {
    const response = await schedulingFetchGet(`student_profiles/${user.id}/notifications_policy`);

    return mapNotificationsPolicyToState(response);
  // fetch policy for coach
  } else if (user.kind === "coach") {
    const response = await schedulingFetchGet(`coach_profiles/${user.id}/notifications_policy`);

    return mapNotificationsPolicyToState(response);
  }

  return {};
};

const updateUserPolicy = async (user: UserModel, notificationsPolicy: UserNotificationsPolicy) => {
  // update policy for coach
  if (user.kind === "coach") {
    return schedulingFetchPatch(
      `coach_profiles/${user.id}/notifications_policy`,
      notificationsPolicy
    );
  // update policy for athlete
  } else if (user.kind === "athlete") {
    return schedulingFetchPatch(
      `student_profiles/${user.id}/notifications_policy`,
      notificationsPolicy
    );
  }
  return Promise.resolve();
};

const generateUserSessionInfoText = (user: UserModel, t: any) => {
  switch (user.kind) {
    case 'coach':
      return t('Receive an email notification when athletes complete actions related to individual sessions - Book, Edit or Cancel');
    case 'athlete':
      return t('Receive an email notification for any action that impacts your calendar, regardless of timing - Book, Edit or Cancel');
    default:
      return t('Receive an email notification when athletes complete actions related to individual sessions - Book, Edit or Cancel');
  }
};

const generateUserProgramsInfoText = (user: UserModel, t: any) => {
  switch (user.kind) {
    case 'coach':
      return t('Receive an email notification when athletes complete actions related to Clinics and Group Programs - Register, Edit, Waitlist or Cancel');
    case 'athlete':
      return t('Receive an email notification when a Coach takes a group registration action - Register, Edit, Waitlist or Cancel');
    default:
      return t('Receive an email notification when athletes complete actions related to Clinics and Group Programs - Register, Edit, Waitlist or Cancel');
  }
};

const generateUserSubscriptionsInfoText = (user: UserModel, t: any) => {
  switch (user.kind) {
    case 'coach':
      return t('Receive an email notification when athletes complete actions related to Packages and Subscriptions - Purchase, Pay, Edit or Cancel');
    case 'athlete':
      return t('Receive an email notification for any action related to Packages and Subscriptions - Purchase, Pay, Edit or Cancel');
    default:
      return t('Receive an email notification when athletes complete actions related to Packages and Subscriptions - Purchase, Pay, Edit or Cancel');
  }
};

const NotificationsEdit = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<UserSettingsModel>({
    pendingInvitationsEmails: false,
    dailyDigestEmails: false,
    weeklyDigestEmails: false,
    defaultEmailNotificationsEnabled: false,
    defaultPushNotificationsEnabled: false,
  });
  const [notificationsPolicy, setNotificationsPolicy] = useState<UserNotificationsPolicy>({
    academySessionEmails: false,
    academyProgramEmails: false,
    academySubscriptionEmails: false,
  });
  const user = useSelector((state: RootState) => state.user.self, shallowEqual);

  const actions = useActions({ fetchNotifications, changeNotifications });
  const [loading, setLoading] = useState(false);
  const [toastAlert, setToastAlert] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const results: Settled<any>[] = await allSettledPromise([
          actions.fetchNotifications(),
          fetchUserPolicy(user),
        ]);

        const [notificationsResult, notificationsPolicyResult] = results;

        // Notifications
        if (notificationsResult.status === "fulfilled") {
          setNotifications(notificationsResult.value);
        } else {
          console.error("Notifications fetch failed:", notificationsResult.reason);
        }

        // Booking policy
        if (notificationsPolicyResult.status === "fulfilled") {
          setNotificationsPolicy(prev => ({
            ...prev,
            ...mapNotificationsPolicyToState(notificationsPolicyResult.value),
          }));
        } else {
          console.error("Booking policy fetch failed:", notificationsPolicyResult.reason);
        }
      } catch (err) {
        console.error("Unexpected error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.id]);


  const onChangeHandler = (param: string, value: boolean) => {
    setNotifications((prevNotifications) => ({
      ...prevNotifications,
      [param]: value,
    }));
  };

  const onNotificationsPolicyChangeHandler = (parameter: string, value: boolean) => {
    setNotificationsPolicy((prev) => ({
      ...prev,
      [parameter]: value,
    }));
  };

  const onSubmitHandler = async () => {
    setLoading(true);

    try {
      const results: Settled<any>[] = await allSettledPromise([
        actions.changeNotifications({ ...notifications, id: user.id }),
        updateUserPolicy(user, notificationsPolicy),
      ]);

      const [notificationsResult, policyResult] = results;

      // check if both request succeeded
      const allSuccess =
        notificationsResult.status === "fulfilled" &&
        policyResult.status === "fulfilled";

      if (allSuccess) {
        setToastAlert(t("Your account was successfully updated."));
      }

      // log the errors
      if (notificationsResult.status === "rejected") {
        console.error("Failed to update notifications:", notificationsResult.reason);
      }

      if (policyResult.status === "rejected") {
        console.error("Failed to update booking policy:", policyResult.reason);
      }

    } finally {
      setLoading(false);
    }
  };

  return (
    <AccountWrap>
      <div className={cls(styles.container, "panel")}>
        <h2 className={styles.panel_title}>{t("Notifications")}</h2>
        <div className={styles.checkboxes_container}>
          <div>
            <h4 className="">{t("Pending invitations reminder")}</h4>

            <CheckBox
              testId={'pendingInvitationsEmails_checkbox'}
              name={'pendingInvitationsEmails'}
              className={styles.checkbox_input}
              checked={notifications && notifications.pendingInvitationsEmails}
              label={t("I want to receive weekly pending invitations reminder")}
              onChange={(checked) =>
                onChangeHandler("pendingInvitationsEmails", checked)
              }
              squareCheckIcons
            />
          </div>

          <div>
            <h4 className="">{t("Daily digest emails")}</h4>

            <CheckBox
              testId={'dailyDigestEmails_checkbox'}
              name={'dailyDigestEmails'}
              className={styles.checkbox_input}
              checked={notifications && notifications.dailyDigestEmails}
              label={t("I want to receive daily digest emails")}
              onChange={(checked) =>
                onChangeHandler("dailyDigestEmails", checked)
              }
              squareCheckIcons
            />
          </div>

          <div>
            <h4 className="">{t("Weekly digest emails")}</h4>

            <CheckBox
              testId={'weeklyDigestEmails_checkbox'}
              name={'weeklyDigestEmails'}
              className={styles.checkbox_input}
              checked={notifications && notifications.weeklyDigestEmails}
              label={t("I want to receive weekly digest emails")}
              onChange={(checked) =>
                onChangeHandler("weeklyDigestEmails", checked)
              }
              squareCheckIcons
            />
          </div>

          <div className={styles.default_notifications_container}>
            <h4 className="">{t("Default notifications settings")}</h4>

            <CheckBox
              testId={'defaultEmailNotificationsEnabled_checkbox'}
              name={'defaultEmailNotificationsEnabled'}
              className={styles.checkbox_input}
              checked={notifications && notifications.defaultEmailNotificationsEnabled}
              label={t("Receive email notifications")}
              onChange={(checked) =>
                onChangeHandler("defaultEmailNotificationsEnabled", checked)
              }
              squareCheckIcons
            />

            <CheckBox
              testId={'defaultPushNotificationsEnabled_checkbox'}
              name={'defaultPushNotificationsEnabled'}
              className={styles.checkbox_input}
              checked={notifications && notifications.defaultPushNotificationsEnabled}
              label={t("Receive push notifications")}
              onChange={(checked) =>
                onChangeHandler("defaultPushNotificationsEnabled", checked)
              }
              squareCheckIcons
            />
          </div>

          {hasFeature(user.features, 'scheduling') && (
            <>
              <h3 className={styles.academy_subtitle}>{t('Academy Settings')}</h3>

              <div className={styles.default_notifications_container}>
                <h4 className={styles.academy_header}>
                  <p>{t("Session notifications")}</p>
                  <ToolTip text={generateUserSessionInfoText(user, t)} selected={true} fontSize="14px" iconColor="gray" />
                </h4>

                <CheckBox
                  testId={'academySessionEmailsEnabled_checkbox'}
                  name={'academySessionEmails'}
                  className={styles.checkbox_input}
                  checked={notificationsPolicy && notificationsPolicy.academySessionEmails}
                  label={t("Receive email notifications")}
                  onChange={(checked) =>
                    onNotificationsPolicyChangeHandler("academySessionEmails", checked)
                  }
                  squareCheckIcons
                />
              </div>

              <div className={styles.default_notifications_container}>
                <h4 className={styles.academy_header}>
                  <p>{t("Program notifications")}</p>
                  <ToolTip text={generateUserProgramsInfoText(user, t)} selected={true} fontSize="14px" iconColor="gray" />
                </h4>

                <CheckBox
                  testId={'academyProgramEmailsEnabled_checkbox'}
                  name={'academyProgramEmails'}
                  className={styles.checkbox_input}
                  checked={notificationsPolicy && notificationsPolicy.academyProgramEmails}
                  label={t("Receive email notifications")}
                  onChange={(checked) =>
                    onNotificationsPolicyChangeHandler("academyProgramEmails", checked)
                  }
                  squareCheckIcons
                />
              </div>

              <div className={styles.default_notifications_container}>
                <h4 className={styles.academy_header}>
                  <p>{t("Package & Subscription notifications")}</p>
                  <ToolTip text={generateUserSubscriptionsInfoText(user, t)} selected={true} fontSize="14px" iconColor="gray" />
                </h4>

                <CheckBox
                  testId={'academySubscriptionEmailsEnabled_checkbox'}
                  name={'academySubscriptionEmails'}
                  className={styles.checkbox_input}
                  checked={notificationsPolicy && notificationsPolicy.academySubscriptionEmails}
                  label={t("Receive email notifications")}
                  onChange={(checked) =>
                    onNotificationsPolicyChangeHandler("academySubscriptionEmails", checked)
                  }
                  squareCheckIcons
                />
              </div>
            </>
          )}

          <Button
            buttonType="primary"
            title={t("Save Changes")}
            buttonStyles={styles.save_button}
            onClickFunc={onSubmitHandler}
            loading={loading}
          />
        </div>
      </div>
      <Toast success={toastAlert} onDone={() => setToastAlert(null)} />
    </AccountWrap>
  );
};

export default NotificationsEdit;
